import { useSyncExternalStore } from 'react';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { idbClearPrefix, idbDelete, idbEntries, idbPut } from './offlineStore';
import { setAccount } from './boardNav';

/**
 * Offline lesen (0.44): Die zuletzt geladenen Stände (Konto, Boardliste, Navigation, zuletzt
 * geöffnete Boards und Karten) liegen je Konto in IndexedDB und werden beim Start eingesetzt –
 * ohne Netz zeigt KaBa dann den letzten Stand. Online lädt TanStack Query wie gewohnt nach.
 * Abmelden, eine abgelaufene Sitzung oder „Offline aus“ löschen alles dieses Kontos auf dem Gerät.
 */
const LAST = 'kaba.lastAccount';
const MAX_BOARDS = 20;
const MAX_CARDS = 300;

let account: string | null = null;
let unsubscribe: (() => void) | null = null;
const timers = new Map<string, ReturnType<typeof setTimeout>>();
/** Schlüssel → Zeitpunkt (für das Aufräumen der ältesten Boards/Karten) */
const stamps = new Map<string, number>();

const prefKey = (acc: string) => `kaba.offline.${acc}`;
export function offlineEnabledFor(acc: string | null): boolean {
  if (!acc) return false;
  try {
    return localStorage.getItem(prefKey(acc)) !== 'false';
  } catch {
    return false;
  }
}

/** Welche Abfragen offline verfügbar sein sollen */
function persistable(key: QueryKey): boolean {
  const [a, b] = key as unknown[];
  if (key.length === 1) return a === 'me' || a === 'boards' || a === 'nav' || a === 'config';
  if (key.length === 2 && typeof b === 'string') return a === 'board' || a === 'card' || a === 'comments';
  return false;
}
const kind = (k: string) => (k.startsWith('["board"') ? 'board' : k.startsWith('["card"') || k.startsWith('["comments"') ? 'card' : 'other');

/** Vor dem ersten Rendern: letzten Stand des zuletzt angemeldeten Kontos einsetzen (höchstens kurz warten) */
export async function hydrateOffline(qc: QueryClient): Promise<string | null> {
  let acc: string | null = null;
  try {
    acc = localStorage.getItem(LAST);
  } catch {
    /* privater Modus */
  }
  if (!acc || !offlineEnabledFor(acc)) return null;
  const entries = await Promise.race([
    idbEntries<{ data: unknown; at: number }>('cache', `${acc}|`),
    new Promise<[string, { data: unknown; at: number }][]>((r) => setTimeout(() => r([]), 1500)),
  ]);
  for (const [k, v] of entries) {
    const key = JSON.parse(k.slice(acc.length + 1)) as QueryKey;
    stamps.set(k, v.at);
    // Als veraltet einsetzen, damit online sofort nachgeladen wird
    if (qc.getQueryData(key) === undefined) qc.setQueryData(key, v.data, { updatedAt: 1 });
  }
  if (!entries.length) return null;
  setLastSaved(Math.max(...entries.map(([, v]) => v.at)));
  // Geräte-Einstellungen (zuletzt geöffnet usw.) gehören sofort zum Konto – nicht erst nach /api/me
  setAccount(acc);
  // Weiter ablegen, auch wenn /api/me gar nicht neu geladen wird
  startOffline(qc, acc);
  return acc;
}

/** Nach der Anmeldung: Änderungen an den gewählten Abfragen laufend ablegen */
export function startOffline(qc: QueryClient, acc: string) {
  if (account === acc && unsubscribe) return;
  stopOffline();
  account = acc;
  try {
    localStorage.setItem(LAST, acc);
  } catch {
    /* egal */
  }
  if (!offlineEnabledFor(acc)) return;
  unsubscribe = qc.getQueryCache().subscribe((ev) => {
    if (ev.type !== 'updated') return;
    const q = ev.query;
    if (!persistable(q.queryKey)) return;
    const k = `${acc}|${JSON.stringify(q.queryKey)}`;
    // Kein Zugriff mehr: auch nicht offline weiter zeigen
    const err = q.state.error as { status?: number } | null;
    if (q.state.status === 'error' && (err?.status === 403 || err?.status === 404)) {
      stamps.delete(k);
      void idbDelete('cache', k);
      return;
    }
    if (q.state.status !== 'success' || q.state.data === undefined) return;
    // Höchstens einmal je 0,8 s schreiben – beim Auslösen den dann neuesten Stand (kein Aushungern bei vielen Ereignissen)
    if (timers.has(k)) return;
    const write = () => {
      timers.delete(k);
      pendingWrites.delete(k);
      if (q.state.data === undefined) return;
      const at = q.state.dataUpdatedAt || Date.now();
      stamps.set(k, at);
      void idbPut('cache', k, { data: q.state.data, at }).then(() => setLastSaved(at));
      prune();
    };
    // Erste Änderung sofort ablegen, weitere höchstens alle 0,8 s (mit dem dann neuesten Stand)
    const last = lastWrite.get(k) ?? 0;
    if (Date.now() - last > 800) {
      lastWrite.set(k, Date.now());
      write();
      return;
    }
    pendingWrites.set(k, () => {
      lastWrite.set(k, Date.now());
      write();
    });
    timers.set(k, setTimeout(pendingWrites.get(k)!, 800));
  });
}

/** Beim Verlassen oder Verbergen der Seite: Ausstehendes sofort ablegen (sonst fehlt der letzte Stand) */
const pendingWrites = new Map<string, () => void>();
const lastWrite = new Map<string, number>();
function flushWrites() {
  for (const [k, w] of [...pendingWrites]) {
    clearTimeout(timers.get(k));
    w();
  }
}
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushWrites);
  document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && flushWrites());
}

function prune() {
  for (const [what, max] of [
    ['board', MAX_BOARDS],
    ['card', MAX_CARDS],
  ] as const) {
    const list = [...stamps.entries()].filter(([k]) => kind(k.slice(k.indexOf('|') + 1)) === what).sort((a, b) => b[1] - a[1]);
    for (const [k] of list.slice(max)) {
      stamps.delete(k);
      void idbDelete('cache', k);
    }
  }
}

export function stopOffline() {
  unsubscribe?.();
  unsubscribe = null;
  for (const t of timers.values()) clearTimeout(t);
  timers.clear();
  pendingWrites.clear();
}

/** Alles dieses Kontos vom Gerät löschen */
export async function wipeOffline(accIn?: string | null, withOutbox = true) {
  let acc = accIn ?? account;
  if (!acc) {
    try {
      acc = localStorage.getItem(LAST);
    } catch {
      /* egal */
    }
  }
  if (!acc) return;
  stopOffline();
  for (const k of [...stamps.keys()]) if (k.startsWith(`${acc}|`)) stamps.delete(k);
  await idbClearPrefix('cache', `${acc}|`);
  if (withOutbox) await idbClearPrefix('outbox', `${acc}|`);
  try {
    if (localStorage.getItem(LAST) === acc) localStorage.removeItem(LAST);
  } catch {
    /* egal */
  }
}

/** Einstellung „Offline auf diesem Gerät“ */
export async function setOfflineEnabled(qc: QueryClient, acc: string, on: boolean) {
  try {
    localStorage.setItem(prefKey(acc), on ? 'true' : 'false');
  } catch {
    /* egal */
  }
  if (on) {
    account = null;
    startOffline(qc, acc);
  } else await wipeOffline(acc, false);
}

export function currentAccount() {
  return account;
}

/* ---------- Letzter abgelegter Stand (für „Stand von …“) ---------- */

let lastSaved = 0;
const savedListeners = new Set<() => void>();
function setLastSaved(at: number) {
  if (at <= lastSaved) return;
  lastSaved = at;
  savedListeners.forEach((l) => l());
}
export function useLastSaved(): number {
  return useSyncExternalStore(
    (cb) => {
      savedListeners.add(cb);
      return () => savedListeners.delete(cb);
    },
    () => lastSaved,
  );
}
